import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import _isEmpty from 'lodash-es/isEmpty';

// sushi
import Button from 'sushiweb/Button';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';

// styles
import { CreateButtonWrapper } from './styles';

// components
import VoucherFields from './Components/Fields/VoucherFields';
import GiftCardFields from './Components/Fields/GiftCardFields';

// contexts
import { FormCacheContext } from '../common/Components/FormTemplate/FormTemplate';
import CreateProgramModalContext from '../../CreateProgramModalContext';

// helpers
import {
  getVoucherProgramBody,
  getGiftCardProgramBody,
} from '../common/helpers/inputHandlers';

// constants
import { VOUCHER, GIFT_CARD } from '../common/Components/constants';
import Card from '../../../../common/components/Card';
import {
  applicableFlowTypes,
  currentFlowTypeEnum,
} from '../../../../common/components/constants';

const ReviewProgram = ({
  creatingProgram,
  createProgram,
  createProgramSuccess,
  reviewProgramDetails,
  program,
  isTwoFactorVisible,
  viewOnlyMode,
  flowType,
}) => {
  const {
    bodyStyle,
    goToPrevStep,
    // goToNextStep,
    stepsVisibility,
    getCachedFormData,
  } = useContext(FormCacheContext);

  const newBodyStyle = {
    ...bodyStyle,
    paddingTop: '2rem',
  };

  const { modalClose, closeModal } = useContext(CreateProgramModalContext);

  // states
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    stepsVisibility(false);
  }, []);

  // sets flag when createProgram is called
  useEffect(() => {
    if (creatingProgram) setFlag(true);
  }, [creatingProgram]);

  useEffect(() => {
    // on recieving successful response
    if (
      (!creatingProgram && flag && createProgramSuccess) ||
      isTwoFactorVisible
    ) {
      closeModal(true);
    } else if (creatingProgram) {
      modalClose.enable();
    } else {
      modalClose.disable();
    }

    return () => modalClose.enable(); // enable closing of modal when not on review page
  }, [flag, creatingProgram, createProgramSuccess, isTwoFactorVisible]);

  const renderFieldsOf = prg => {
    switch (prg) {
      case GIFT_CARD:
        return <GiftCardFields reviewProgramDetails={reviewProgramDetails} />;
      case VOUCHER:
        return (
          <VoucherFields
            reviewProgramDetails={reviewProgramDetails}
            currentFlow={flowType}
          />
        );
      default:
        return <div>No fields available!</div>;
    }
  };

  const handleCreateBtnClick = () => {
    let programBody = {};

    const formData = getCachedFormData();

    switch (program) {
      case VOUCHER:
        programBody = getVoucherProgramBody(formData);
        break;

      case GIFT_CARD:
        programBody = getGiftCardProgramBody(formData);
        break;

      default:
        programBody = {};
    }

    if (_isEmpty(programBody)) return;

    modalClose.disable();

    const workspaceID = reviewProgramDetails?.workspace_id;
    const programID = reviewProgramDetails?.program_id;

    programBody = { workspace_id: workspaceID, program_id: programID };

    createProgram({ programBody });
  };

  const handleBackBtnClick = () => {
    stepsVisibility(true);
    goToPrevStep();
  };

  return (
    <>
      <Card.Body style={newBodyStyle}>{renderFieldsOf(program)}</Card.Body>
      <CreateButtonWrapper>
        <Button
          appearance='outline'
          icon={<ChevronLeft />}
          disabled={creatingProgram}
          iconAlign='left'
          btnColor='red'
          onClick={handleBackBtnClick}
        >
          Go back
        </Button>
        <Button
          onClick={handleCreateBtnClick}
          loading={creatingProgram}
          icon={<ChevronRight />}
          iconAlign='right'
          disabled={viewOnlyMode}
        >
          Create meal plan
        </Button>
      </CreateButtonWrapper>
    </>
  );
};

ReviewProgram.propTypes = {
  program: PropTypes.string.isRequired,
  createProgram: PropTypes.func,
  creatingProgram: PropTypes.bool,
  isTwoFactorVisible: PropTypes.bool,
  createProgramSuccess: PropTypes.bool,
  reviewProgramDetails: PropTypes.objectOf(PropTypes.any),
  viewOnlyMode: PropTypes.bool,
  flowType: PropTypes.oneOf(applicableFlowTypes),
};

ReviewProgram.defaultProps = {
  createProgram: _noop,
  creatingProgram: false,
  createProgramSuccess: false,
  isTwoFactorVisible: false,
  reviewProgramDetails: {},
  viewOnlyMode: false,
  flowType: currentFlowTypeEnum.FLOW_TYPE_PPI,
};

export default ReviewProgram;
